import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  Truck, 
  Search, 
  FileText, 
  AlertTriangle, 
  Clock, 
  CheckCircle,
  ArrowRight,
  Bus,
  Package,
  MapPin,
  TrendingUp,
  Target,
  Users,
  Route
} from 'lucide-react';

export default function MarchesPublicsTransportPage() {
  return (
    <>
      <SEO 
        title="Marchés Publics Transport : Appels d'Offres Logistique et Mobilité"
        description="Comment trouver des marchés publics de transport et logistique. Guide complet : transport de personnes, livraisons, déménagements, transport scolaire. Conseils pour transporteurs et logisticiens."
        path="/marches-publics-transport"
      />
      <div className="min-h-screen bg-white">
        <Header />
        
        <Breadcrumb 
          items={[
            { label: 'Marchés publics transport' }
          ]} 
        />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-900 via-cyan-900 to-slate-900 py-16 text-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-300 rounded-full text-sm font-medium mb-6">
                <Truck className="w-4 h-4" />
                Secteur Transport & Logistique
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Marchés Publics Transport : Appels d'Offres Logistique et Mobilité
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                Transport scolaire, livraisons, déménagements, navettes... Les collectivités ont des besoins 
                constants en solutions de mobilité. Accédez aux opportunités de votre secteur.
              </p>
              <Link to="/">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                  <Search className="w-5 h-5 mr-2" />
                  Trouver des marchés transport
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { value: '6 Md€', label: 'transport public/an', icon: TrendingUp },
                { value: '+12%', label: 'marchés verts', icon: Target },
                { value: '8000+', label: 'appels d\'offres/an', icon: FileText },
                { value: '4 ans', label: 'durée moyenne', icon: Clock }
              ].map((stat, i) => (
                <div key={`item-${i}`} className="bg-white rounded-xl p-6 text-center shadow-sm">
                  <stat.icon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-sm text-slate-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            
            {/* Section 1 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Truck className="w-8 h-8 text-blue-600" />
                Quels types de marchés publics de transport ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Les <strong>marchés publics de transport</strong> englobent toutes les prestations de déplacement 
                  de personnes et de marchandises pour le compte des administrations. Ces marchés offrent 
                  une visibilité pluriannuelle appréciable pour les transporteurs.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                {[
                  {
                    icon: Bus,
                    title: 'Transport de personnes',
                    items: ['Transport scolaire', 'Navettes personnel', 'Transport adapté (PMR)', 'Sorties et voyages scolaires']
                  },
                  {
                    icon: Package,
                    title: 'Transport de marchandises',
                    items: ['Livraisons courrier/colis', 'Transport de repas', 'Approvisionnement fournitures', 'Messagerie express']
                  },
                  {
                    icon: Route,
                    title: 'Services logistiques',
                    items: ['Déménagements administratifs', 'Stockage et archivage', 'Manutention et levage', 'Gestion de flottes']
                  },
                  {
                    icon: MapPin,
                    title: 'Mobilité durable',
                    items: ['Vélos en libre-service', 'Autopartage', 'Covoiturage organisé', 'Navettes électriques']
                  }
                ].map((category, i) => (
                  <div key={`item-${i}`} className="bg-slate-50 rounded-xl p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <category.icon className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-slate-900">{category.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {category.items.map((item, j) => (
                        <li key={j} className="flex items-center gap-2 text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Target className="w-8 h-8 text-blue-600" />
                Qui sont les acheteurs publics en transport ?
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  {
                    title: 'Régions & Départements',
                    description: 'Autorités organisatrices du transport scolaire et interurbain. Marchés d\'envergure.',
                    volume: '50% des marchés'
                  },
                  {
                    title: 'Communes & Intercommunalités',
                    description: 'Navettes, transport à la demande, mobilité urbaine et périscolaire.',
                    volume: '30% des marchés'
                  },
                  {
                    title: 'Établissements publics',
                    description: 'Hôpitaux (transport patients), universités, administrations centrales.',
                    volume: '20% des marchés'
                  }
                ].map((buyer, i) => (
                  <div key={`item-${i}`} className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100">
                    <h3 className="font-semibold text-slate-900 mb-2">{buyer.title}</h3>
                    <p className="text-slate-600 text-sm mb-3">{buyer.description}</p>
                    <div className="text-blue-600 font-medium">{buyer.volume}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 3 - Conseils */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                <Users className="w-8 h-8 text-blue-600" />
                Réussir vos marchés de transport
              </h2>
              <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Points clés du mémoire technique</h3>
                    <ul className="space-y-3">
                      {[
                        'Flotte de véhicules adaptée (âge, normes)',
                        'Organisation et planification des circuits',
                        'Qualification des conducteurs',
                        'Gestion des imprévus et remplacements',
                        'Engagement environnemental (Euro 6, électrique)'
                      ].map((item, i) => (
                        <li key={`item-${i}`} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Critères environnementaux</h3>
                    <ul className="space-y-3">
                      {[
                        'Véhicules propres (électriques, GNV, hybrides)',
                        'Formation éco-conduite',
                        'Optimisation des trajets (km/passager)',
                        'Compensation carbone',
                        'Certificats Crit\'Air'
                      ].map((item, i) => (
                        <li key={`item-${i}`} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-blue-200 flex-shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Alert Box */}
            <div className="mb-16 bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex gap-4">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">Transport scolaire : calendrier spécifique</h3>
                  <p className="text-slate-600">
                    Les marchés de transport scolaire sont généralement publiés entre <strong>janvier et avril</strong> 
                    pour une mise en service à la rentrée de septembre. Anticipez votre veille pour ne pas 
                    manquer ces opportunités pluriannuelles (3 à 5 ans).
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-slate-900 rounded-2xl p-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">
                Développez votre activité transport avec les marchés publics
              </h2>
              <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                Recevez les appels d'offres transport de votre région et de votre spécialité 
                dès leur publication.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                    Créer mon compte gratuit
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/trouver-appels-offres">
                  <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                    Voir les marchés du jour
                  </Button>
                </Link>
              </div>
            </div>

          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPath="/marches-publics-transport" />

        <Footer />
      </div>
    </>
  );
}
